import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useExternalSupplierStorefrontsControllerListMine,
  useExternalSupplierStorefrontsControllerListMyPrices,
  useExternalSupplierStorefrontsControllerSetPrice,
  useExternalSupplierStorefrontsControllerRequestOffering,
  getExternalSupplierStorefrontsControllerListMyPricesQueryKey,
  getExternalSupplierStorefrontsControllerListMineQueryKey,
} from "@semo/api-contracts";

/**
 * 내가 열려 있는 쇼핑몰과, 지금 화면에 보이는 품목의 몰 단가.
 *
 * 쇼핑몰은 마스터가 «이 업체 이 품목» 을 허용해야 생긴다. 그래서 열은 내가 정하는 게
 * 아니라 서버가 알려 준다 — 허용이 없으면 열 자체가 없다.
 *
 * **단가를 넣는 순간 그 쇼핑몰에 노출된다.** 비워 두면 허용은 돼 있어도 안 팔린다.
 * 값이 없는 것과 0원은 다르다.
 */
export function useMyStorefrontPrices(visibleItemIds: string[]) {
  const queryClient = useQueryClient();

  const { data: mineRes } = useExternalSupplierStorefrontsControllerListMine();
  const storefronts = useMemo(() => mineRes?.data ?? [], [mineRes]);

  // 표 한 페이지분만 묻는다. 전체를 받으면 품목 수만큼 커지는데 화면에 쓰는 건
  // 지금 보이는 줄뿐이다.
  const itemIds = useMemo(
    () => [...new Set(visibleItemIds)].filter(Boolean).sort(),
    [visibleItemIds],
  );
  const joined = itemIds.join(",");

  const { data: pricesRes } =
    useExternalSupplierStorefrontsControllerListMyPrices(
      { itemIds: joined },
      {
        query: {
          // 열이 없으면(허용된 쇼핑몰 0개) 물어볼 이유가 없다.
          enabled: storefronts.length > 0 && itemIds.length > 0,
        },
      },
    );

  /**
   * `storefrontId:itemId` → 단가.
   *
   * **키가 있다는 것 자체가 «마스터가 허용했다» 는 뜻**이다(응답은 내 offering 줄만
   * 담는다). 값이 null 이면 허용은 됐는데 아직 안 적은 것이고, 키가 아예 없으면
   * 허용되지 않은 품목이다 — 이 둘을 구분하지 못하면 화면이 빈 칸 하나로 두 가지
   * 다른 상황을 보여 주게 되고, 공급사는 적어도 안 팔리는 이유를 알 수 없다.
   */
  const priceMap = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const row of pricesRes?.data ?? []) {
      map.set(`${row.storefrontId}:${row.itemId}`, row.price ?? null);
    }
    return map;
  }, [pricesRes]);

  const setPriceMutation = useExternalSupplierStorefrontsControllerSetPrice({
    mutation: {
      onSuccess: async (res) => {
        const listed = res?.data?.isListed;
        toast.success(
          listed
            ? "단가를 저장했습니다. 쇼핑몰에 노출됩니다."
            : "단가를 저장했습니다.",
        );
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              getExternalSupplierStorefrontsControllerListMyPricesQueryKey({
                itemIds: joined,
              }),
          }),
          // 「허용 N · 노출 M」 숫자가 같이 움직인다.
          queryClient.invalidateQueries({
            queryKey:
              getExternalSupplierStorefrontsControllerListMineQueryKey(),
          }),
        ]);
      },
      onError: (e) => toast.error(e?.message ?? "단가 저장에 실패했습니다"),
    },
  });

  const requestOffering =
    useExternalSupplierStorefrontsControllerRequestOffering({
      mutation: {
        onSuccess: (res) => {
          toast.success(
            res?.data?.created
              ? "승인 요청을 보냈습니다. 세모 관리자가 승인하면 가격을 넣을 수 있습니다."
              : "이미 대기 중인 요청이 있습니다.",
          );
        },
        onError: (e) => toast.error(e?.message ?? "요청에 실패했습니다"),
      },
    });

  return {
    storefronts,
    priceOf: (storefrontId: string, itemId: string) =>
      priceMap.get(`${storefrontId}:${itemId}`) ?? null,
    /** 마스터가 이 몰에 이 품목을 허용했는가 */
    isAllowed: (storefrontId: string, itemId: string) =>
      priceMap.has(`${storefrontId}:${itemId}`),
    requestOffering: (storefrontId: string, itemId: string) =>
      requestOffering.mutate({ storefrontId, data: { itemId } }),
    setPrice: (storefrontId: string, itemId: string, price: number) =>
      setPriceMutation.mutate({ storefrontId, itemId, data: { price } }),
    isSaving: setPriceMutation.isPending,
  };
}
